import * as aspSettings from './aspSettings';
import * as settings from '../common/settings';
import * as utils from '../common/utils';
import { aspMngr } from './AspMngr';
import * as aspObjects from './aspObjects';
import { AppConfig } from '../app.config';
import { AspObject, ObjectType } from './AspObject';
import { MapSelectionTool } from './tools/MapSelectionTool';
import { MapEventsTool } from './tools/MapEventsTool';
import { IMapMngr, IObjectsDrawer, IObjectsPool } from './interfaces';
import { SensorsCoreDrawer } from './SensorsCoreDrawer';
import { DisplayObject } from './DisplayObject';
import { TracksCoreDrawer } from './TracksCoreDrawer';
import { PlotsCoreDrawer } from './PlotsCoreDrawer';
import { SpecialMeasurmentsTool } from './tools/SpecialMeasurmentsTool';
import { CreateTool } from './tools/CreateTool';
import { FreeHandTool } from './tools/FreeHandTool';
import { RulersDrawer, RulersPool } from './rulers';
import { AcousticVectorDrawer } from './acousticVectorDrawer';
import { FreeDrawingsDrawer } from './free-drawings';
import { TempDrawingsDrawer } from './temp-drawings';
import { Point } from '../common/Point';
import * as consts from '../common/consts';
import * as enums from '../common/enums';
import { WindowOnFocus } from '../../app/common/ZIndexMngr';
import { SensorObject } from 'src/app/asp/SensorObject';
import { SensorsDrawer } from 'src/app/asp/SensorsDrawer';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
/* global ol, NeWMI */

export function getNewmiLoadingConfig(is3D?: boolean) {
  if (AppConfig.settings.map.esri) {
    const esriLoadingConfig = is3D ?
      {
        url: "./assets/NeWMI",
        engine: NeWMI.Engines.esri4,
        viewType: NeWMI.Map.Enums.EViewType.Globe
      } :
      {
        url: "./assets/NeWMI",
        engine: NeWMI.Engines.esri4,
      }
    return esriLoadingConfig;
  }
  else {
    const olLoadingConfig = {
      url: "./assets/NeWMI",
      engine: NeWMI.Engines.openLayers,
    }
    return olLoadingConfig;
  }
}

export var mainMapMngr: MapMngr;

const BG_MAP_HIDDEN_OPACITY = 0.0;

export class DrawersFactory {

  createSensorsDrawer(mapMngr: IMapMngr, objectsPool: IObjectsPool, parentLayer) {
    return new SensorsCoreDrawer(mapMngr, objectsPool, parentLayer);
  }

  createPlotsDrawer(mapMngr: IMapMngr, objectsPool: IObjectsPool, parentLayer) {
    return new PlotsCoreDrawer(mapMngr, objectsPool, parentLayer);
  }

  createTracksDrawer(mapMngr: IMapMngr, objectsPool: IObjectsPool, parentLayer) {
    return new TracksCoreDrawer(mapMngr, objectsPool, parentLayer);
  }

  createAcousticVectorDrawer(mapMngr: IMapMngr, objectsPool: IObjectsPool, parentLayer) {
    return new AcousticVectorDrawer(mapMngr, objectsPool, parentLayer);
  }
}

export interface IMapEventsSubscriber {
  // (x,y) in pixles or null in case mouse left the map
  onMouseMove(pixel: Point): void;

  onExtentChange(): void;
}

export class MapMngr implements IMapMngr {

  private static allMapMngrs: Array<MapMngr> = new Array();

  private static drawersFactory: DrawersFactory = new DrawersFactory();

  private _shown = false;

  private bgMapOpacity: number;

  private mapExtent: any;
  private mapEventsSubscribers: Array<IMapEventsSubscriber> = new Array();

  // Mouse screen (pixels) location
  private mousePixel: Point = null;

  private groupLayer: any;
  private theMap: any;
  private bgMapLayer: Array<any> = [];
  private bgMapVisible: any;
  private currentCursor: string = 'default';

  private mapSelectionTool: MapSelectionTool = null;
  private specialMeasurmentsTool: SpecialMeasurmentsTool = null;
  private createTool: CreateTool = null;
  private editTool: any = null;
  private freeHandTool: FreeHandTool = null;
  private mapEventsTool: MapEventsTool = null;

  public _sensorsDrawer: SensorsCoreDrawer = null;
  protected _plotsDrawer: PlotsCoreDrawer = null;
  protected _sensorTracksDrawer: TracksCoreDrawer = null;
  protected _tracksDrawer: TracksCoreDrawer = null;
  protected _rulersDrawer: RulersDrawer = null;
  protected _acVecDrawer: AcousticVectorDrawer = null;
  protected _freeDrawingsDrawer: FreeDrawingsDrawer = null;
  protected _tempDrawingsDrawer: TempDrawingsDrawer = null;

  protected objectsDrawers: Map<ObjectType, IObjectsDrawer> = new Map<ObjectType, IObjectsDrawer>();

  protected dynamicCenterId = null;

  protected mapPanning: boolean = false;
  protected cursorBeforePan: string;

  private mapIndex: number = 0;

  constructor(private _isMainMap: boolean = false, protected _is3D: boolean = false) {
    if (_isMainMap) {
      mainMapMngr = this;
      this._shown = true;
      NeWMI.defaults.hitTolerance = 5;
      NeWMI.defaults.noGL = false;
      NeWMI.defaults.ellipsePts = 90;
      NeWMI.defaults.toolBackgroundColor = "rgba(0, 0, 30, 0.2)";
      NeWMI.defaults.performAutoScrollingForPointGeo = false;
      // !!! enable debug
      // NeWMI.defaults["debug"] = true;
      NeWMI.defaults.appLayersSingleContext = true;
    }
    settings.userSettings.getMapIndexObs().subscribe((Number) => {
      if (Number != undefined) {
        this.switchMap(Number);
        this.mapIndex = Number;
      }
    });
    MapMngr.allMapMngrs.push(this);

  }

   
  isMainMap(): boolean {
    return this._isMainMap;
  }

  isMapPanning(): boolean {
    return this.mapPanning;
  }

  is3D(): boolean {
    return this._is3D;
  }

  static setDrawersFactory(factory: DrawersFactory) {
    MapMngr.drawersFactory = factory;
  }

  static createOfflineMapLayer(mapSettings) {
    const mapLayer = NeWMI.Create.localCacheTiledLayer(getNewmiLoadingConfig().engine,
      {
        url: mapSettings.url,
        originX: mapSettings.origin[0],
        originY: mapSettings.origin[1],
        extent: mapSettings.extent,
        // lods: mapSettings.lods
      });

    return mapLayer;
  }

  public shown() {
    return this._shown;
  }

  public setShown(shown) {
    this._shown = shown;
  }

  public refresh(): void {
    if (typeof this.theMap == 'undefined')
      return;
    if (!this.shown)
      return;

    this.redrawBackground();

    if (this.dynamicCenterId != null) {
      let obj = aspMngr.getObject(this.dynamicCenterId);
      if (obj != null)
        this.setMapCenter(obj.geometry);
    }

    for (let drawer of this.objectsDrawers.values()) {
      drawer.periodicUpdate();
    }

    this.redrawMap();

    this.specialMeasurmentsTool.refresh();
  }

  public redrawBackground() {
    // Set background map opacity to match display filter
    let mapVisible = settings.userSettings.filterOn[settings.DisplayFilter.Maps];
    let mapOpacity = settings.userSettings.bgMapOpacity;
    if (mapVisible != this.bgMapVisible || mapOpacity != this.bgMapOpacity) {
      this.bgMapVisible = mapVisible;
      this.bgMapOpacity = mapOpacity;
      if (AppConfig.settings.map.esri) {
        if (this.bgMapLayer) {
          for (let index = 0; index < this.bgMapLayer.length; index++) {
            // !!! you need to see what layer is visible in user settings...
            this.bgMapLayer[index].opacity = (this.bgMapVisible ? this.bgMapOpacity : BG_MAP_HIDDEN_OPACITY);
          }
        }
      }
      else {
        for (let index = 0; index < this.bgMapLayer.length; index++) {
          this.bgMapLayer[index].setOpacity(this.bgMapVisible ? this.bgMapOpacity : BG_MAP_HIDDEN_OPACITY);
        }
      }
    }
  }

  public redrawMap() {
    this.theMap.layersMgr.refreshAllLayers();
  }

  private switchMap(index: number) {
    if (this.mapIndex == index)
      return;
    this.theMap.layersMgr.removeStaticLayer(this.bgMapLayer[this.mapIndex].newmiProps.id);
    if (index != undefined)
      this.mapIndex = index;
    else {
      this.mapIndex++;
      if (this.mapIndex >= this.bgMapLayer.length)
        this.mapIndex = 0;
    }
    this.theMap.layersMgr.insertStaticLayer(this.bgMapLayer[this.mapIndex]);
    const BG_MAP: Array<string> = AppConfig.settings.map.name.split(',');
    document.documentElement.style.setProperty("--mapBackground", AppConfig.settings.map[BG_MAP[this.mapIndex]].reference.backgroundColor);
  }

  public async initMap(map) {
    const BG_MAP: Array<string> = AppConfig.settings.map.name.split(',');
    this.theMap = map;

    // map extents from config...
    // let mapXCenter = AppConfig.settings.map[BG_MAP[this.mapIndex]].xCenter;
    // let mapYCenter = AppConfig.settings.map[BG_MAP[this.mapIndex]].yCenter;
    // let mapWidth = AppConfig.settings.map[BG_MAP[this.mapIndex]].width;
    // let mapHeight = AppConfig.settings.map[BG_MAP[this.mapIndex]].height;
    // this.mapExtent = new NeWMI.Geometry.Rectangle({
    //   "xCenter": undefined,
    //   "yCenter": undefined,
    //   "width": undefined,
    //   "height": undefined
    // });

    if (AppConfig.settings.map.esri) {
      this.theMap.core.constraints.rotationEnabled = this._is3D;
      this.theMap.core.constraints.snapToZoom = false;
    }


    
    if (!this._is3D) { // newmi-tbd: handle 3D light/brightness
      this.bgMapVisible = settings.userSettings.filterOn[settings.DisplayFilter.Maps];
      this.bgMapOpacity = settings.userSettings.bgMapOpacity;
      for (let index = 0; index < BG_MAP.length; index++) {
        let mapReference = AppConfig.settings.map[BG_MAP[index]].reference;
        if (AppConfig.settings.map[BG_MAP[index]].offline == 'true')
          this.bgMapLayer.push(MapMngr.createOfflineMapLayer(AppConfig.settings.map[BG_MAP[index]]));
        else {
          if (mapReference.protocol.toLocaleLowerCase() == "wmts") {
            this.bgMapLayer.push(NeWMI.Create.wmtsLayer(getNewmiLoadingConfig().engine, mapReference));
          }
          else if (mapReference.protocol.toLocaleLowerCase() == "arcgis") {
            this.bgMapLayer.push(NeWMI.Create.wmtsLayer(getNewmiLoadingConfig().engine, mapReference));
          }
          else if (mapReference.protocol.toLocaleLowerCase() == "bantal") {
            if( AppConfig.appMap)
                mapReference.styles= AppConfig.appMap
                else
                mapReference.styles= ""
            this.bgMapLayer.push(NeWMI.Create.localCacheVectorTiledLayer(getNewmiLoadingConfig().engine, mapReference));
          }
          else
            this.bgMapLayer.push(NeWMI.Create.wmsLayer(getNewmiLoadingConfig().engine, mapReference));
        }
        if (AppConfig.settings.map.esri)
          this.bgMapLayer[index].opacity = (this.bgMapVisible ? this.bgMapOpacity : BG_MAP_HIDDEN_OPACITY);
        else
          this.bgMapLayer[index].setOpacity(this.bgMapVisible ? this.bgMapOpacity : BG_MAP_HIDDEN_OPACITY);
      }
    }
    this.theMap.layersMgr.insertStaticLayer(this.bgMapLayer[this.mapIndex]);
    document.documentElement.style.setProperty("--mapBackground", AppConfig.settings.map[BG_MAP[this.mapIndex]].reference.backgroundColor);

    let extent = settings.userSettings.mapExtent;
    if (extent.xCenter !== undefined && extent.yCenter !== undefined)
      this.setMapExtent(extent);
    else
      this.theMap.setExtent(this.mapExtent);

    this.mapSelectionTool = new MapSelectionTool(this);
    this.theMap.toolsMgr.add(this.mapSelectionTool.specialTool);

    this.specialMeasurmentsTool = new SpecialMeasurmentsTool(this);
    this.specialMeasurmentsTool.init(map);
    this.specialMeasurmentsTool.on('addMeasure', this.onAddMeasure.bind(this));
    this.theMap.toolsMgr.add(this.specialMeasurmentsTool);

    if (this._isMainMap) {
      aspSettings.setMainMapSelectionTool(this.mapSelectionTool);

      this.createTool = new CreateTool(this);
      this.createTool.on('creationFinished', this.onCreateFinished.bind(this));
      this.theMap.toolsMgr.add(this.createTool);

      this.freeHandTool = new FreeHandTool(this);
      this.freeHandTool.on('creationFinished', this.onCreateFinished.bind(this));
      this.theMap.toolsMgr.add(this.freeHandTool);

      this.editTool = new NeWMI.Tool.EditTool();
      this.theMap.toolsMgr.add(this.editTool);

      this.mapEventsTool = new MapEventsTool(this);
      this.theMap.toolsMgr.add(this.mapEventsTool);
    }

    map.on(NeWMI.Map.Enums.EMapEvents.ExtentChange, this.onMapEvent.bind(this));
    map.on(NeWMI.Map.Enums.EMapEvents.PanStart, this.onMapEvent.bind(this));
    map.on(NeWMI.Map.Enums.EMapEvents.PanEnd, this.onMapEvent.bind(this));
    map.on(NeWMI.Map.Enums.EMapEvents.KeyDown, this.onMapEvent.bind(this));

    this.activateDefaultTool();

    this.theMap.selectionMgr.on("selectionChanged", this.onSelectionChanged.bind(this));

    this.groupLayer = new NeWMI.Layer.GroupLayer(utils.isWebGLSupported());
    this.theMap.layersMgr.insertAppLayer(this.groupLayer);

    this.createDrawers();

    if (!this._is3D) {
      let mapDivElem: Element = this.theMap.div;
      if (mapDivElem) {
        mapDivElem.addEventListener('mouseenter', evt => {
          let mouseEvent = evt as MouseEvent;
          this.mousePixel = { x: mouseEvent.offsetX, y: mouseEvent.offsetY };
          aspSettings.setMapCoordinate(this.mouseMapLocation());
          this.notifyMapEventsSubscribers(true);
        });
        mapDivElem.addEventListener('mouseleave', evt => {
          this.mousePixel = null;
          // don't clear coord display aspSettings.setMapCoordinate(null);
          this.notifyMapEventsSubscribers(true);
        });
        mapDivElem.addEventListener('mousemove', evt => {
          let mouseEvent = evt as MouseEvent;
          this.mousePixel = { x: mouseEvent.offsetX, y: mouseEvent.offsetY };
          localStorage.setItem(consts.LOCAL_STORAGE_HEIGHT_KEY, '');
          if (mouseEvent.button == 0)
            aspSettings.setMapCoordinate(this.mouseMapLocation());
          this.notifyMapEventsSubscribers(true);
        });
      }
    }
  }

  onMapEvent(evt) {
    switch (evt.eventType) {
      case NeWMI.Map.Enums.EMapEvents.ExtentChange:
        this.mapExtent = evt.extent;
        if (this._isMainMap) {
          settings.userSettings.setMapExtent(
            new settings.MapExtent(this.mapExtent.xCenter, this.mapExtent.yCenter, this.mapExtent.width, this.mapExtent.height));
        }
        if (this.mouseInMap())
          aspSettings.setMapCoordinate(this.mouseMapLocation());
        this.notifyMapEventsSubscribers(false, true);
        break;
      case NeWMI.Map.Enums.EMapEvents.PanStart:
        if (typeof evt.button == 'undefined' || evt.button == 2) {
          this.mapPanning = true;
          this.cursorBeforePan = this.getCursor();
          this.setCursor('grabbing');
        }
        break;
      case NeWMI.Map.Enums.EMapEvents.PanEnd:
        if (typeof evt.button == 'undefined' || evt.button == 2) {
          this.mapPanning = false;
          this.setCursor(this.cursorBeforePan);
        }
        break;
      case NeWMI.Map.Enums.EMapEvents.KeyDown:
        if (!WindowOnFocus)
          this.handleKey(evt);
        break;
    }
  }

  public handleKey(evt) {
    let keyEvt: KeyboardEvent = evt;
    let mapExtent = this.getMapExtent();
    switch (keyEvt.key) {
      case "ArrowUp":
      case "ArrowDown":
        {
          let delta = mapExtent.height / 10.0;
          if (keyEvt.key == "ArrowDown")
            delta *= -1;
          mapExtent.yCenter += delta;
          this.setMapExtent(mapExtent);
          evt.coreEvt.stopPropagation();
          break;
        }
      case "ArrowLeft":
      case "ArrowRight":
        {
          let delta = mapExtent.width / 10.0;
          if (keyEvt.key == "ArrowLeft")
            delta *= -1;
          mapExtent.xCenter += delta;
          this.setMapExtent(mapExtent);
          evt.coreEvt.stopPropagation();
          break;
        }
      case "+":
      case "-":
        {
          if (keyEvt.key == "+") {
            mapExtent.width /= 1.05;
            mapExtent.height /= 1.05;
          }
          else {
            mapExtent.width *= 1.05;
            mapExtent.height *= 1.05;
          }
          this.setMapExtent(mapExtent);
          evt.coreEvt.stopPropagation();
          break;
        }
      case "Escape":
        {
          this.activateDefaultTool();
        }
    }
  }

  public allDrawersInitialized(): boolean {
    for (let drawer of this.objectsDrawers.values()) {
      if (!drawer.isInitialized())
        return false;
    }
    return true;
  }

  // public resetSensorDrawer() {
  //   this._sensorsDrawer.objectsLayer.clearAll(true);
  //   this._sensorsDrawer.objectsLayer.refresh();


  //   // this._sensorsDrawer = MapMngr.drawersFactory.createSensorsDrawer(this, aspObjects.sensorsPool, this.groupLayer);
  //   // if (this._sensorsDrawer)
  //   //   this.objectsDrawers.set(aspObjects.sensorsPool.getObjectType(), this._sensorsDrawer);

  //   // this._sensorsDrawer.createObjectsFromPool();
  //   // this._sensorsDrawer.subscribeObjectsPool();
  //   // this._sensorsDrawer.periodicUpdate();

  //   // this.createObjects();
  //   // this.subscribeObjects();

  //   // this.redrawMap();
  // }

  protected createDrawers() {
    this._freeDrawingsDrawer = new FreeDrawingsDrawer(this, this.groupLayer);
    this.objectsDrawers.set(ObjectType.FreeDrawing, this._freeDrawingsDrawer);

    this._acVecDrawer = MapMngr.drawersFactory.createAcousticVectorDrawer(this, aspObjects.acousticVectorPool, this.groupLayer);
    if (this._acVecDrawer)
      this.objectsDrawers.set(aspObjects.acousticVectorPool.getObjectType(), this._acVecDrawer);

    this._sensorsDrawer = MapMngr.drawersFactory.createSensorsDrawer(this, aspObjects.sensorsPool, this.groupLayer);
    if (this._sensorsDrawer)
      this.objectsDrawers.set(aspObjects.sensorsPool.getObjectType(), this._sensorsDrawer);

    this._plotsDrawer = MapMngr.drawersFactory.createPlotsDrawer(this, aspObjects.plotsPool, this.groupLayer);
    if (this._plotsDrawer)
      this.objectsDrawers.set(aspObjects.plotsPool.getObjectType(), this._plotsDrawer);

    this._tracksDrawer = MapMngr.drawersFactory.createTracksDrawer(this, aspObjects.tracksPool, this.groupLayer);
    if (this._tracksDrawer)
      this.objectsDrawers.set(aspObjects.tracksPool.getObjectType(), this._tracksDrawer);

    if (!this._is3D) {
      this._rulersDrawer = new RulersDrawer(this, this.groupLayer);
      this.objectsDrawers.set(ObjectType.Ruler, this._rulersDrawer);
    }

    if (this._isMainMap) {
      this._tempDrawingsDrawer = new TempDrawingsDrawer(this, this.groupLayer);
      this.objectsDrawers.set(ObjectType.TempDrawing, this._tempDrawingsDrawer);

      this.createObjects();
      this.subscribeObjects();
    }
  }

  public get sensorsDrawer() {
    return this._sensorsDrawer;
  }

  public get plotsDrawer() {
    return this._plotsDrawer;
  }

  public get sensorTracksDrawer() {
    return this._sensorTracksDrawer;
  }

  public get tracksDrawer() {
    return this._tracksDrawer;
  }

  public get rulersDrawer() {
    return this._rulersDrawer;
  }

  public get freeDrawingsDrawer() {
    return this._freeDrawingsDrawer;
  }

  public get acousticVectorDrawer() {
    return this._acVecDrawer;
  }

  public setMapCenter(coord: Point) {
    this.mapExtent = new NeWMI.Geometry.Rectangle({
      "xCenter": coord.x,
      "yCenter": coord.y,
      "width": this.mapExtent.width,
      "height": this.mapExtent.height
    });

    this.theMap.setCenter(this.mapExtent.xCenter, this.mapExtent.yCenter);
  }

  public setCursor(cursor: string) {
    this.currentCursor = cursor;
    this.theMap.setCursor(cursor);
  }

  public restoreCursor() {
    this.setCursor('default');
  }

  public getCursor(): string {
    return this.currentCursor;
  }

  public getMapElement(): Element {
    return this.theMap.div;
  }

  public getMap(): any {
    return this.theMap;
  }

  public getMapExtent(): settings.MapExtent {
    let extent: settings.MapExtent = new settings.MapExtent();
    extent.xCenter = this.mapExtent.xCenter;
    extent.yCenter = this.mapExtent.yCenter;
    extent.width = this.mapExtent.width;
    extent.height = this.mapExtent.height;
    return extent;
  }

  public setMapExtent(extent: settings.MapExtent, rotation?: number) {
    this.mapExtent = new NeWMI.Geometry.Rectangle({
      "xCenter": extent.xCenter,
      "yCenter": extent.yCenter,
      "width": extent.width,
      "height": extent.height
    });

    this.theMap.setExtent(this.mapExtent);

    if (rotation !== undefined)
      this.theMap.setRotation(rotation);
  }

  public objectsAtLocation(point: NeWMI.Geometry.Point, onlyHookable?: boolean, inDrawer?: IObjectsDrawer): AspObject[] {
    const factorTolerance = 4;
    const hitToleranceMapUnits = this.theMap.selectionMgr.getHitToleranceMapUnits();
    const searchGeo = new NeWMI.Geometry.Rectangle({
      xCenter: point.x, yCenter: point.y,
      width: hitToleranceMapUnits.width * factorTolerance,
      height: hitToleranceMapUnits.height * factorTolerance
    });

    let dispObjects: DisplayObject[] = null;
    for (let drawer of (inDrawer ? [inDrawer] : this.objectsDrawers.values())) {
      if (!onlyHookable || drawer.getObjectsPool().hookable()) {
        const layer = drawer.getLayer();
        if (layer && layer.newmiProps.visible) {
          let foundObjects = layer.search(searchGeo, true /* ExactSearch */, false /* IgnoreCustomSearch */, true /* IsSearchForSelection */) as DisplayObject[];
          if (dispObjects)
            dispObjects = dispObjects.concat(foundObjects);
          else
            dispObjects = foundObjects;

          if (drawer instanceof SensorsDrawer) {
            if (foundObjects.length == 0) {
              let acousticDraw = drawer as SensorsDrawer;
              let hitResults = acousticDraw.geometricObjectsAtLocation(point, this.theMap);
              if (hitResults.length > 0)
                dispObjects = hitResults;
            }
            else {
              foundObjects.forEach(obj => {
                let sensorObj = obj.getAspObject() as SensorObject;
                sensorObj.geometryMenuVisible = false;
              });
            }
          }
        }
      }
    }
    if (dispObjects == null || dispObjects.length == 0)
      return null;
    let objects: AspObject[] = new Array();
    for (let i = 0; i < dispObjects.length; i++) {
      let obj = dispObjects[i].getAspObject();
      if (obj.isValid() && obj.active())
        objects.push(obj);
    }
    return objects;
  }

  private onSelectionChanged(evt: NeWMI.EventObject) {
    let newSelectedIds = new Set();

    if (evt.object.type != NeWMI.Selection.Enums.ESelectionType.Clear) {
      let selection;
      if (evt.object.type != NeWMI.Selection.Enums.ESelectionType.New)
        newSelectedIds = new Set(aspSettings.selectedIds);
      for (let i = 0; i < evt.object.selection.length; i++) {
        selection = evt.object.selection[i];
        for (let intIdx = 0; intIdx < selection.objects.length; intIdx++) {
          let obj: DisplayObject = selection.objects[intIdx];
          if (obj.getAspObject().objectsPool.hookable()) {
            if (evt.object.type == NeWMI.Selection.Enums.ESelectionType.New) {
              newSelectedIds.add(obj.id);
            }
            else if (evt.object.type == NeWMI.Selection.Enums.ESelectionType.Add) {
              if (!newSelectedIds.has(obj.id))
                newSelectedIds.add(obj.id);
            }
            else if (evt.object.type == NeWMI.Selection.Enums.ESelectionType.Remove) {
              if (newSelectedIds.has(obj.id))
                newSelectedIds.delete(obj.id);
            }
          }
        }
      }
    }

    // redraw all un-selected objects and newly selected objects

    let addedIds = new Set([...newSelectedIds].filter(x => !aspSettings.selectedIds.has(x)));
    let removedIds = new Set([...aspSettings.selectedIds].filter(x => !newSelectedIds.has(x)));

    // console.info('added selection:', addedIds, 'removed selection:', removedIds);

    aspSettings.setSelectedIds(newSelectedIds);
    aspMngr.changeHooked(addedIds, removedIds);
    MapMngr.redrawAllMaps();
  }

  // Return true if event was handled and should not be passed to the browser
  public handleKeyboardEvent(evt: KeyboardEvent): boolean {
    // aspMngr.toggleHookedObjectsHistory(); // this thing anables hostory on all hooked objects...

    if (settings.userSettings.userPerm == enums.Permission.USER)
      return false;

    if (evt.code.toLocaleLowerCase() == 'keyt') { // enable trail for all selected objects
      aspMngr.toggleHookedObjectsHistory();
      return false;
    }

    if (evt.code.toLocaleLowerCase() == 'enter') {
      for (let id of aspSettings.selectedIds) {
        let obj = this.getObject(id).getAspObject();
        if (obj != null)
          if (obj.objectType == ObjectType.Track)
            obj.objectsPool.handleUserAction(consts.USER_ACTION_ARO, obj, this);
      }
    }

    if (evt.ctrlKey) { // ctrl key is pressed down
      if (evt.shiftKey) { // shift key is pressed down
        if (evt.code.toLocaleLowerCase() == 'keym') { // remove all rulers
          RulersPool.getSelf().confirmAndDeleteAllObjects();
          return true;
        }
      }

      if (evt.code.toLocaleLowerCase() == 'keys') { // for suspect
        aspMngr.changeHostilityOfTracks(5);
        return true;
      }
      if (evt.code.toLocaleLowerCase() == 'keyf') { // for friend
        aspMngr.changeHostilityOfTracks(3);
        return true;
      }
      if (evt.code.toLocaleLowerCase() == 'keyu') { // for unknown
        aspMngr.changeHostilityOfTracks(1);
        return true;
      }
      if (evt.code.toLocaleLowerCase() == 'keyh') { // for hostile
        aspMngr.changeHostilityOfTracks(6);
        return true;
      }
      if (evt.code.toLocaleLowerCase() == 'keym') { // create ruler
        this.activateSpecialMeasurmentsTool();
        return true;
      }
      if (evt.code.toLocaleLowerCase() == 'keyy') { // create ruler
        aspMngr.forceSendPolyToSmart();
        return true;
      }
    }
    return false;
  }

  public pixelToMapLocation(pixel: Point): Point {
    if (this.theMap && this.theMap.core) {
      if (AppConfig.settings.map.esri || this.theMap.core.getCoordinateFromPixel([pixel.x, pixel.y])) {
        let convertedPoint = this.theMap.conversionsSvc.toMap(pixel.x, pixel.y);
        return { x: convertedPoint.x, y: convertedPoint.y };
      }
    }
    return null;
  }

  public mouseMapLocation(): Point {
    if (this.theMap && this.theMap.core && this.mouseInMap())
      return this.pixelToMapLocation(this.mousePixel);
    return null;
  }

  public mouseInMap(): boolean {
    return this.shown() && (this.mousePixel != null);
  }

  public getMapSelectionTool(): MapSelectionTool {
    return this.mapSelectionTool;
  }

  public subscribeObjects() {
    for (let drawer of this.objectsDrawers.values()) {
      drawer.subscribeObjectsPool();
    }
  }

  public unsubscribeObjects() {
    for (let drawer of this.objectsDrawers.values()) {
      drawer.unsubscribeObjectsPool();
    }
  }

  // Delete all display objects
  public clearObjects() {
    for (let drawer of this.objectsDrawers.values()) {
      drawer.deleteAllObjects();
    }
    this.dynamicCenterId = null;
    this.redrawMap();
  }

  // Create display objects for all ASP objects
  public createObjects() {
    for (let drawer of this.objectsDrawers.values()) {
      drawer.createObjectsFromPool();
    }
    this.redrawMap();
  }

  public static redrawAllBackground() {
    for (let mapMngr of MapMngr.allMapMngrs) {
      if (mapMngr.shown())
        mapMngr.redrawBackground();
    }
  }

  public static redrawAllMaps() {
    for (let mapMngr of MapMngr.allMapMngrs) {
      if (mapMngr.shown())
        mapMngr.redrawMap();
    }
  }

  handleUserAction(action: number, obj: AspObject): boolean {
    for (let drawer of this.objectsDrawers.values()) {
      if (drawer.handleUserAction(action, obj))
        return true;
    }
    return false;
  }

  public getObject(objectId): DisplayObject {
    for (let drawer of this.objectsDrawers.values()) {
      let obj = drawer.getObject(objectId);
      if (obj)
        return obj;
    }
    return null;
  }

  public getDynamicCenterId() {
    return this.dynamicCenterId;
  }

  public setDynamicCenterId(objectId) {
    this.dynamicCenterId = objectId;
  }

  // activate the default selection tool
  public activateDefaultTool() {
    this.createToolCallback = null;
    this.theMap.toolsMgr.currentTool && this.theMap.toolsMgr.currentTool.deactivate();
    this.theMap.toolsMgr.activate(this.mapSelectionTool.specialTool);
    this.restoreCursor();
  }

  public activateDefaultToolForce() {
    this.createToolCallback = null;
    this.theMap.toolsMgr.activate(this.mapSelectionTool.specialTool);
    this.restoreCursor();
  }

  // activate the special measurements tool
  public activateSpecialMeasurmentsTool() {
    // check if single object is hooked, and if so, use it
    let fromObject: AspObject = null;
    if (aspSettings.selectedIds.size == 1) {
      let objId = aspSettings.selectedIds.values().next().value;
      if (this.getObject(objId).isVisible())
        fromObject = aspMngr.getObject(objId);
    }
    this.specialMeasurmentsTool.setFromObject(fromObject);

    this.theMap.toolsMgr.activate(this.specialMeasurmentsTool);
  }



  private createToolGeometryType: NeWMI.Geometry.Enums.EGeometryType;
  private createToolCallback: (geometry: any) => any = null;
  private createToolMultiShape: boolean = false;

  private callCreateToolCallback(geometry: any) {
    if (this.createToolCallback) {
      this.createToolCallback.call(this, geometry);
      if (!this.createToolMultiShape)
        this.createToolCallback = null;
    }
  }

  // activate the creation tool, callback will be called upon finish
  public activateCreateTool(geometryType: NeWMI.Geometry.Enums.EGeometryType, callback: (geometry: any) => any, multiShape?: boolean) {
    if (this._isMainMap && this.createToolCallback == null) {
      this.createToolGeometryType = geometryType;
      this.createToolCallback = callback;
      this.createToolMultiShape = multiShape;
      if (geometryType == NeWMI.Geometry.Enums.EGeometryType.Multipoint) {
        this.theMap.toolsMgr.activate(this.freeHandTool);
      }
      else {
        this.theMap.toolsMgr.activate(this.createTool);
        if (multiShape)
          this.createTool.setMultiShape(multiShape);
        this.createTool.start({ geometryType: geometryType });
      }
    }
  }

  public activateEditTool(editGeometry: any) {
    this.theMap.toolsMgr.activate(this.editTool);
    this.editTool.start({ geometry: editGeometry });
  }

  public getEditFinishedGeometry(): NeWMI.Geometry.Base.AGeometry {
    return this.editTool.finish();
  }

  // createion tool finished
  private onCreateFinished(evt) {
    this.callCreateToolCallback(evt.object);
    if (this.createToolMultiShape) {
      if (this.createToolGeometryType != NeWMI.Geometry.Enums.EGeometryType.Multipoint)
        this.createTool.start({ geometryType: this.createToolGeometryType });
    }
    else {
      this.activateDefaultTool();
    }
  }

  // special measurments tool finished
  private onAddMeasure(evt) {
    RulersPool.getSelf().createRuler(evt.geometry, evt.fromObjectId, evt.toObjectId);
    this.activateDefaultTool();
    this.redrawMap();
  }

  // activate the map events tool, callback will be called upon tool events
  public activateMapEventsTool(callback: (evt: NeWMI.Map.Events.MouseEvents) => any, eventsNeeded: NeWMI.Map.Enums.EMapEvents[], cursor?: string) {
    this.mapEventsTool.setCallback(callback, eventsNeeded);
    this.mapEventsTool.setCursor(cursor ? cursor : 'crosshair');
    this.theMap.toolsMgr.activate(this.mapEventsTool);
  }

  public defaultToolActive(): boolean {
    if (typeof this.theMap == 'undefined')
      return false;
    return this.theMap.toolsMgr.currentTool == this.mapSelectionTool.specialTool;
  }

  public specialMeasurmentsToolActive(): boolean {
    if (typeof this.theMap == 'undefined')
      return false;
    return this.theMap.toolsMgr.currentTool == this.specialMeasurmentsTool;
  }

  public createToolActive(): boolean {
    if (typeof this.theMap == 'undefined')
      return false;
    return this.theMap.toolsMgr.currentTool == this.createTool || this.theMap.toolsMgr.currentTool == this.freeHandTool;
  }

  addMapEventsSubscriber(subs: IMapEventsSubscriber) {
    let index = this.mapEventsSubscribers.indexOf(subs);
    if (index < 0)
      this.mapEventsSubscribers.push(subs);
  }

  delMapEventsSubscriber(subs: IMapEventsSubscriber) {
    let index = this.mapEventsSubscribers.indexOf(subs);
    if (index >= 0)
      this.mapEventsSubscribers.splice(index, 1);
  }

  private notifyMapEventsSubscribers(mouseMove?: boolean, extentChange?: boolean) {
    for (let subs of this.mapEventsSubscribers) {
      if (mouseMove)
        subs.onMouseMove(this.mousePixel);
      if (extentChange)
        subs.onExtentChange();
    }
  }

  public getSizeByZoomValue(): number {
    return (this.getMap().getZoom() / this.getMap().getMaxZoom()) * 2;
  }
}
